import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import MapView from '@arcgis/core/views/MapView';
import Draw from '@arcgis/core/views/draw/Draw';
import Graphic from '@arcgis/core/Graphic';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer.js';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { ComponentContainer } from 'golden-layout';
import { MatMenuTrigger } from '@angular/material/menu';
import Point from '@arcgis/core/geometry/Point';
import { MatDialog } from '@angular/material/dialog';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import Color from '@arcgis/core/Color';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import { Store } from '@ngrx/store';
import Layer from '@arcgis/core/layers/Layer';
import Geometry from '@arcgis/core/geometry/Geometry';
import Query from '@arcgis/core/rest/support/Query';
import Search from '@arcgis/core/widgets/Search';
import { ArxThematicWidget } from '@arxit/esri/widgets/ArxThematic';

import { GoldenLayoutContainerInjectionToken, WidgetHostDirective } from '../../../_directives/widget-host.directive';
import { EsDialogEditComponent } from '../../_dialogs/es-dialog-edit/es-dialog-edit.component';
import { EvenementSpontane, IdentifyEvenementSpontane, IEvenementSpontane } from '../../../_models/EvenementSpontane';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import { EsService } from '../../../_services/http/es.service';
import { EsInfoComponent } from './es-info/es-info.component';
import { PgmService } from '../../../_services/http/pgm.service';
import { MainStoreState } from '../../../_store/mainStoreState';
import { loadRapportSousSystemesAction } from '../../../_store/RapportSousSystemeStore/rapport-sous-systeme.store';
import { RapportSousSystemeService } from '../../../_services/http/rapport-sous-systeme.service';
import { ConfirmService } from '../../../_services/local/confirm.service';
import { ConfigService } from '../../../_services/local/config.service';
import { RefreshMapService } from '../../../_services/http/refresh-map.service';
import { MessageService } from '../../../_services/local/message.service';

@Component({
  selector: 'app-widget-map-edit',
  templateUrl: './map-edit.component.html',
  styleUrls: ['./map-edit.component.scss'],
})
export class MapEditComponent extends WidgetHostDirective implements OnInit, AfterViewInit, OnDestroy {
  // HTML
  @ViewChild('mapViewNode', { static: true }) private mapViewEl!: ElementRef;
  // @ViewChild('esInfoComponent', { static: true })
  // private esInfoComponent!: FlyingContainerComponent;
  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger!: MatMenuTrigger;
  EsInfoComponent = EsInfoComponent;

  // INTERNAL
  private onDestroy$ = new Subject<boolean>();
  private channelEfp = new BroadcastChannel('map-efp');
  // private readonly esInfoMessagesDefault = [
  //   "Effectuer un click gauche pour définir l'emplacement d'un nouvel évènement",
  //   'ou pour sélectionner un évènement existant',
  // ];
  // private readonly esInfoMessagesSegment = ['Effectuer un click gauche pour sélectionner', "un tronçon impacté par l'évènement"];
  // private readonly esInfoMessagesPosition = ["Effectuer un click gauche pour redéfinir l'emplacement"];

  // PORTAL
  private readonly mapId = this.configService.Config.mapEftId;

  // MAP
  private mapView!: MapView;
  private draw!: Draw;
  private firstTimeAddingGraphic = true;
  menuTopLeftPosition = { x: '0', y: '0' };

  // PGM
  private pgmConfig = this.configService.Config.layers ? this.configService.Config.layers.pgmEFT : null;
  private pgmLayers: Layer[] = [];
  loading$ = new BehaviorSubject<boolean>(false);

  // ES
  private currentEsGraphic!: Graphic;
  private isEdition = false;
  private shouldUpdateEsPosition = false;
  private esConfig = this.configService.Config.layers ? this.configService.Config.layers.es : null;
  private esGraphicsLayer = new GraphicsLayer({
    id: 'esGraphicLayer',
    visible: true,
  });
  private simpleMarker = new SimpleMarkerSymbol({
    outline: new SimpleLineSymbol({
      color: Color.fromHex('#FF0000'),
      style: 'solid',
      width: 2,
    }),
    style: 'cross',
    size: 20,
  });

  // SEGMENT
  private segmentConfig = this.configService.Config.layers ? this.configService.Config.layers.segment : null;
  private segmentLayer = new FeatureLayer({
    url: this.segmentConfig?.url,
    outFields: ['*'],
    renderer: new SimpleRenderer({
      symbol: new SimpleLineSymbol({
        color: [0, 0, 0, 0.01],
      }),
    }),
  });
  private segmentLayerView: __esri.FeatureLayerView | null = null;
  private segmentLayerViewHightlightHandle: __esri.Handle | null | undefined = null;
  private highlightedSegment: Graphic | null = null;
  // private segmentGraphicsLayer = new GraphicsLayer({
  //   id: 'segmentGraphicLayer',
  //   visible: true,
  // });
  // private segmentSimpleLineSelection = new SimpleLineSymbol({
  //   color: Color.fromHex('#0000FF'),
  //   width: 2,
  // });

  esDeclared: EvenementSpontane | null = null;
  menuLoading = false;
  openRepports = false;

  constructor(
    @Inject(GoldenLayoutContainerInjectionToken) container: ComponentContainer,
    elRef: ElementRef,
    renderer: Renderer2,
    viewContainerRef: ViewContainerRef,
    store: Store<MainStoreState>,
    private esService: EsService,
    private pgmService: PgmService,
    private rapportSousSystemService: RapportSousSystemeService,
    private confirmService: ConfirmService,
    private dialog: MatDialog,
    private configService: ConfigService,
    private refreshMapService: RefreshMapService,
    private messageService: MessageService
  ) {
    super(container, elRef.nativeElement, renderer, viewContainerRef, store);

    this.channelEfp.addEventListener('message', (evt) => {
      if (evt.data && evt.data.type === 'apply-pgm') {
        this.applyPgm();
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.store.dispatch(loadRapportSousSystemesAction());
    this.rapportSousSystemService.refreshHubConnection();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // this.esInfoComponent.data = this.esInfoMessagesDefault;
    // this.esInfoComponent.show();

    this.calculateHeight(this.container.height);

    this.initializeMap().then(async () => {
      const arxThematic = new ArxThematicWidget({
        view: this.mapView,
        shouldApplyGrayscaleWhenUnchecked: true,
        thematics: this.configService.Config.thematicsEFT,
        position: 'start',
        resizable: true,
        collapsible: true,
        open: false,
      });
      this.mapView.ui.add(arxThematic, 'top-left');
      const searchWidget = new Search({
        view: this.mapView,
        sources: this.configService.Config.search.sources,
        includeDefaultSources: false,
      });
      this.mapView.ui.add(searchWidget, { position: 'top-trailing', index: 1 });

      this.initializeDraw();

      this.refreshMapService.refreshed$.subscribe(() => {
        EsriHelpers.refreshView(this.mapView);
      });
      await this.refreshMapService.refreshHubConnection();

      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.mapView.destroy();

    this.channelEfp.close();

    this.rapportSousSystemService.closeHubConnection();
  }

  declareEs(mapPoint: Point): void {
    this.isEdition = false;
    this.togglePgmLayer(false);

    this.esDeclared = new EvenementSpontane({
      id: -1,
      geometry: mapPoint,
      actif: true,
      idSegment: this.highlightedSegment?.attributes.idsegment,
    });

    this.updateEsGraphic(mapPoint);
    this.toggleEsDialog();

    // this.esInfoComponent.hide();
    // this.esInfoComponent.data = this.esInfoMessagesSegment;
    // this.esInfoComponent.show();

    // if (this.firstTimeAddingGraphic) {
    //   setTimeout(() => {
    //     this.firstTimeAddingGraphic = false;
    //     this.mapView.zoom += 0.5;
    //     this.mapView.goTo(this.currentEsGraphic);
    //   }, 200);
    // }
  }

  editEs(): void {
    if (!this.esDeclared || !this.esDeclared.idSegment) {
      return;
    }

    this.isEdition = true;
    this.toggleEsDialog();
  }

  deleteEs(): void {
    if (!this.esDeclared || !this.esDeclared.id) {
      return;
    }

    this.confirmService
      .open(`Voulez-vous terminer l'évènement spontané`, this.esDeclared.id.toString(), this.esService.delete(this.esDeclared.id))
      .then((res) => {
        if (res && this.esDeclared && this.esDeclared.id) {
          this.esDeclared = null;
        }
      });
  }

  // selectSegment(item: number | undefined): void {
  //   if (this.esDeclared) {
  //     this.esDeclared.idSegment = item;
  //     this.toggleEsDialog();
  //   }
  // }

  async initializeMap(): Promise<void> {
    try {
      this.mapView = await EsriHelpers.GenerateMapAsync(
        this.mapViewEl.nativeElement,
        this.mapId,
        [this.segmentLayer, this.esGraphicsLayer],
        { activateIdentify: false }
      );

      await this.segmentLayer.when();

      this.mapView.on('click', (evt) => {
        console.debug('click on map', evt);

        // let res = this.leftClickOnMapWhenEsSelected(evt);
        // if (res) {
        //   return;
        // }

        const res = this.leftClickOnMapWhenUpdatePosition(evt);
        if (res) {
          return;
        }

        this.leftClickOnMap(evt);
      });

      this.mapView.map.layers.forEach((l) => {
        if (l instanceof MapImageLayer) {
          // PGM
          if (this.pgmConfig && l.url === this.pgmConfig.url) {
            this.pgmLayers.push(l);
          }

          // ES
          if (this.esConfig && l.url === this.esConfig.url) {
            this.esConfig.layer = l;
          }
        }
      });

      this.segmentLayerView = await this.mapView.whenLayerView(this.segmentLayer);
      this.mapView.on('pointer-move', (event) => {
        const opts: __esri.MapViewHitTestOptions = {
          include: this.segmentLayer,
        };
        this.mapView.hitTest(event, opts).then((response) => {
          if (response.results.length && response.results[0].type === 'graphic') {
            const graphic = response.results[0].graphic;

            if (this.segmentLayerViewHightlightHandle && this.highlightedSegment?.getObjectId() !== graphic.getObjectId()) {
              this.segmentLayerViewHightlightHandle?.remove();
              this.segmentLayerViewHightlightHandle = null;
              return;
            }

            if (this.segmentLayerViewHightlightHandle) {
              return;
            }

            this.segmentLayerViewHightlightHandle = this.segmentLayerView?.highlight(graphic);
            this.highlightedSegment = graphic;
          } else {
            this.segmentLayerViewHightlightHandle?.remove();
            this.segmentLayerViewHightlightHandle = null;
            this.highlightedSegment = null;
          }
        });
      });
    } catch (error) {
      console.debug('EsriLoader: ', error);
    }
  }

  public applyEfp(): void {
    this.loading$.next(true);
    this.pgmService.updatePgmMode(false).subscribe(async (result) => {
      this.openRepports = true;
      this.loading$.next(false);
    });
  }

  public reset(): void {
    this.shouldUpdateEsPosition = false;
    this.openRepports = false;

    this.esGraphicsLayer.removeAll();
    this.esDeclared = null;

    this.togglePgmLayer(true);
  }

  private leftClickOnMap(evt: any): void {
    console.debug('leftClickOnMap', evt);

    this.menuLoading = true;
    this.esDeclared = null;

    this.findSelectedEs(evt.mapPoint)
      .then((graphic) => {
        if (graphic) {
          const mapEs: IdentifyEvenementSpontane = graphic.attributes;
          const idEs = Number(mapEs.ID_ES);

          this.esService.retrieve(idEs).subscribe((es) => {
            if (es) {
              this.esDeclared = new EvenementSpontane(es);
              if (this.esDeclared.features) {
                this.currentEsGraphic = EsriHelpers.SagtFeatureToGraphic(this.esDeclared.features[0]);
              }
              console.debug('left click : es trouvé', this.esDeclared);
              if (!this.highlightedSegment) {
                this.triggerMenuAtClickPosition(evt);
              }
            }

            this.menuLoading = false;
          });
        } else {
          this.menuLoading = false;
        }
      })
      .catch((error) => {
        console.warn('findSelectedEs', error);
        this.menuLoading = false;
      });

    if (evt.native.button === 0 && this.highlightedSegment) {
      this.triggerMenuAtClickPosition(evt);
    }
  }

  private triggerMenuAtClickPosition(evt: any): void {
    evt.native.preventDefault();
    evt.stopPropagation();

    this.menuTopLeftPosition.x = evt.native.clientX + 'px';
    this.menuTopLeftPosition.y = evt.native.clientY + 'px';
    this.matMenuTrigger.menuData = { item: evt.mapPoint };
    this.matMenuTrigger.toggleMenu();
  }

  // private leftClickOnMapWhenEsSelected(evt: any): boolean {
  //   console.debug('leftClickOnMapWhenEsSelected', evt);

  //   if (evt.native.button === 0 && this.esDeclared && !this.shouldUpdateEsPosition) {
  //     console.debug('leftClickOnMapWhenEsSelected: es selected', evt, this.esDeclared);

  //     evt.native.preventDefault();
  //     evt.stopPropagation();

  //     this.findSelectedSegment(evt.mapPoint).then((segmentId) => {
  //       console.debug('findSelectedSegment', segmentId);
  //       if (segmentId == null) {
  //         this.matMenuTrigger.menuData = { item: undefined };
  //       } else {
  //         this.matMenuTrigger.menuData = { item: segmentId };
  //       }

  //       this.menuTopLeftPosition.x = evt.native.clientX + 'px';
  //       this.menuTopLeftPosition.y = evt.native.clientY + 'px';
  //       this.matMenuTrigger.toggleMenu();
  //     });

  //     return true;
  //   }
  //   return false;
  // }

  private leftClickOnMapWhenUpdatePosition(evt: any): boolean {
    console.debug('leftClickOnMapWhenUpdatePosition', evt);

    if (evt.native.button === 0 && this.shouldUpdateEsPosition && this.esDeclared && this.esDeclared.idSegment) {
      console.debug('leftClickOnMapWhenUpdatePosition: es declared', evt, this.esDeclared);

      evt.native.preventDefault();
      evt.stopPropagation();

      this.updateEsGraphic(evt.mapPoint);
      this.toggleEsDialog(true);
      this.shouldUpdateEsPosition = false;

      return true;
    }
    return false;
  }

  private initializeDraw(): void {
    if (!this.mapView) {
      return;
    }

    this.draw = new Draw({
      view: this.mapView,
    });

    document.getElementById('line-button')?.addEventListener('click', () => {
      this.mapView?.graphics.removeAll();

      const action = this.draw.create('polygon');
      this.mapView?.focus();

      action.on(['vertex-add', 'vertex-remove', 'cursor-update', 'draw-complete'], (event) => {
        if (!this.mapView) {
          return;
        }

        this.mapView.graphics.removeAll();

        const polygon = {
          type: 'polygon', // autocasts as Polygon
          rings: event.vertices,
          spatialReference: this.mapView.spatialReference,
        };

        const graphic = new Graphic({
          geometry: polygon,
          symbol: {
            // @ts-ignore
            type: 'simple-fill', // autocasts as SimpleFillSymbol
            color: 'purple',
            style: 'solid',
            outline: {
              // autocasts as SimpleLineSymbol
              color: 'white',
              width: 1,
            },
          },
        });
        this.mapView.graphics.add(graphic);
      });
    });
  }

  public applyPgm(): void {
    this.esDeclared = null;
    this.config.item = null;

    this.pgmService.updatePgmMode(true).subscribe(() => {
      this.messageService.success('Application réussie');
    });
  }

  private handleEsDialogClosing(es: EvenementSpontane | { updatePosition: boolean }): void {
    if (es) {
      if ('updatePosition' in es) {
        this.shouldUpdateEsPosition = true;
        // this.esInfoComponent.hide();
        // this.esInfoComponent.data = this.esInfoMessagesPosition;
        // this.esInfoComponent.show();
      } else if ('idSegment' in es) {
        this.createOrUpdateEs(es);
        this.reset();
      }
    } else {
      this.reset();
    }
  }

  private createOrUpdateEs(es: EvenementSpontane): void {
    const newEs = es.format();
    newEs.features = [EsriHelpers.GraphicToSagtFeature(this.currentEsGraphic)];

    this.loading = true;
    const observable = this.isEdition && newEs.id ? this.esService.update(newEs) : this.esService.create(newEs);
    observable.subscribe((result) => {
      if (result !== undefined) {
        this.togglePgmLayer(true);
        this.zoomToPgms(newEs);
      }

      this.loading = false;
    });
  }

  private updateEsGraphic(point: Geometry): void {
    this.esGraphicsLayer.removeAll();
    this.currentEsGraphic = new Graphic({
      geometry: point,
      symbol: this.simpleMarker,
    });
    this.esGraphicsLayer.add(this.currentEsGraphic);
  }

  private async findSelectedEs(mapPoint: Point): Promise<Graphic | null> {
    try {
      console.debug('findSelectedEs (identify)', mapPoint);

      const params = new IdentifyParameters({
        geometry: mapPoint,
        returnGeometry: true,
        tolerance: 2,
        mapExtent: this.mapView.extent,
        layerOption: 'all',
      });
      const results = await EsriHelpers.identify(this.esConfig?.layer, params);
      return results.length > 0 ? results[0].feature : null;
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  // private async findSelectedSegment(mapPoint: Point): Promise<number | undefined> {
  //   let segmentId = undefined;

  //   try {
  //     if (!this.configService.Config.layers.segment.idSegmentField) {
  //       return segmentId;
  //     }

  //     const params = new Query({
  //       geometry: mapPoint,
  //       returnGeometry: true,
  //       outFields: ['*'],
  //       distance: 4,
  //     });
  //     const results = await EsriHelpers.query(this.segmentConfig!.url, params);
  //     this.segmentGraphicsLayer.removeAll();

  //     if (results.features.length > 0) {
  //       const firstFeature = results.features[0];
  //       const selectedSegment = new Graphic({
  //         geometry: firstFeature.geometry,
  //         attributes: firstFeature.attributes,
  //         symbol: this.segmentSimpleLineSelection,
  //       });
  //       this.segmentGraphicsLayer.add(selectedSegment);
  //       segmentId = firstFeature.attributes[this.configService.Config.layers.segment.idSegmentField];
  //     } else {
  //       this.messageService.info('Création ES : aucun segment trouvé.');
  //     }
  //   } catch (error) {
  //     console.warn(error);
  //   }

  //   return segmentId;
  // }

  private toggleEsDialog(updatePosition = false): void {
    // this.esInfoComponent.hide();

    const dialog = this.dialog.open(EsDialogEditComponent, {
      data: {
        es: this.esDeclared,
        updatePosition,
      },
    });
    dialog.afterClosed().subscribe(this.handleEsDialogClosing.bind(this));
  }

  private togglePgmLayer(show: boolean): void {
    this.pgmLayers.forEach((l) => (l.visible = show));
  }

  private async zoomToPgms(es: IEvenementSpontane): Promise<void> {
    const query = new Query();
    query.where = es.pgm ? `ID_PGM=${es.pgm.id}` : '1=1';
    query.returnGeometry = true;
    const promises = this.configService.Config.layers.pgmEFT.layerIds.map((id) =>
      EsriHelpers.query(`${(this.pgmLayers[0] as __esri.FeatureLayer).url}/${id}`, query)
    );
    const featureSets = await Promise.all(promises);
    await this.mapView.goTo(featureSets.map((x) => x.features).flat(1));
  }
}
